// OVO-253 — site-first order creation.
//
// The form is partner-first: the Partner select sits at the top, and once a
// partner is chosen the Pickup / Delivery blocks offer the partner's Site
// catalog (OVO-101) as autocompletes instead of free-text addresses. When the
// selected pickup Site has collection boxes (OVO-206), a box select appears
// (preselected when the site has exactly one). Each side can still fall back
// to a custom address ("Use a different address"), with an optional
// "Save as new site" that creates the Site right before the order.
//
// Everything optional (phones, emails, time windows, durations, dimensions,
// notes, types, tickets, partner refs, products) hides behind one global
// "Advanced" toggle.
//
// E2E compat note: the classic specs fill custom pickup/delivery FIRST and
// select the partner LAST — so picking a partner only auto-switches a side to
// site mode when that side's custom fields are still untouched.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Button,
  Spin,
  Row,
  Col,
  DatePicker,
  TimePicker,
  InputNumber,
  Select,
  Radio,
  Checkbox,
  Divider,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { ReactSVG } from 'react-svg';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import PhoneInput from 'react-phone-input-2';

import './Order.scss';
import { DATE_BACKEND_FORMAT, DAY_FORMAT, TIME_FORMAT, ORDER_TYPES } from '../../constants';
import AddressInput from '../Inputs/AddressInput';
import { useDispatch, useSelector } from 'react-redux';
import { doGetPartners } from '../../modules/partners/stores/thunk';
import { selectAllPartners } from '../../modules/partners/stores/slice';
import { isSingleTenant } from '../../config/tenantConfig';
import { getPartnerItemTypes } from '../../modules/partners/api';
import { listSitesForPartner, createSite } from '../../modules/sites/api';
import { listCollectionPointsForPartner } from '../../modules/collection-points/api';
import TagInput from '../Elements/TagInput';

const OrderForm = ({ isFetching, handleSubmit }) => {
  // OVO-266 — bilingual labels/tooltips for the per-parcel durations.
  const { t: tOrders } = useTranslation('orders');
  const [form] = Form.useForm();
  const [countryPickupPhone, setCountryPickupPhone] = useState();
  const [countryDeliveryPhone, setCountryDeliveryPhone] = useState();
  const [types, setTypes] = useState([]);
  const [ticketNames, setTicketNames] = useState([]);
  const [orderType, setOrderType] = useState(ORDER_TYPES.STANDARD);
  const [productTypes, setProductTypes] = useState([]);

  // OVO-253 — site-first state. 'site' = pick from the partner catalog,
  // 'custom' = free-text name + address (the pre-OVO-253 behaviour).
  const [pickupMode, setPickupMode] = useState('custom');
  const [deliveryMode, setDeliveryMode] = useState('custom');
  const [partnerSites, setPartnerSites] = useState([]);
  const [partnerBoxes, setPartnerBoxes] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const partners = useSelector(selectAllPartners);

  const selectedPartnerId = Form.useWatch('partnerId', form);

  // #172 — a single-tenant instance (CT) has one implicit partner: the field is
  // hidden and filled here, so the partner-scoped pickers (sites, boxes, item
  // types) still load and the order is created for it.
  useEffect(() => {
    if (isSingleTenant && partners.length === 1 && !form.getFieldValue('partnerId')) {
      form.setFieldsValue({ partnerId: partners[0].id });
    }
  }, [partners, form]);
  const productDetailsKnown = Form.useWatch('productDetailsKnown', form);
  const pickupSiteIdWatch = Form.useWatch('pickupSiteId', form);

  // Fetch PRODUCT item types when partner changes
  useEffect(() => {
    if (!selectedPartnerId) {
      setProductTypes([]);
      return;
    }
    getPartnerItemTypes(selectedPartnerId).then(({ response }) => {
      const items = Array.isArray(response) ? response : [];
      setProductTypes(items.filter((t) => t.category === 'PRODUCT' && t.isActive));
    });
  }, [selectedPartnerId]);

  // OVO-253 — fetch the partner's Sites + collection boxes; decide per-side
  // whether to enter site mode (only when the custom fields are untouched, so
  // the address-first flow keeps working).
  useEffect(() => {
    form.setFieldsValue({ pickupSiteId: undefined, deliverySiteId: undefined, collectionPointId: undefined });
    if (!selectedPartnerId) {
      setPartnerSites([]);
      setPartnerBoxes([]);
      setPickupMode('custom');
      setDeliveryMode('custom');
      return;
    }
    listSitesForPartner(selectedPartnerId).then(({ response }) => {
      setPartnerSites((Array.isArray(response) ? response : []).filter((s) => !s.isArchived));
    });
    listCollectionPointsForPartner(selectedPartnerId).then(({ response }) => {
      setPartnerBoxes((Array.isArray(response) ? response : []).filter((p) => !p.isArchived));
    });
    const current = form.getFieldsValue(['pickupName', 'pickupAddress', 'deliveryName', 'deliveryAddress']);
    if (!current.pickupName && !current.pickupAddress?.address) setPickupMode('site');
    if (!current.deliveryName && !current.deliveryAddress?.address) setDeliveryMode('site');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId]);

  // Boxes at the selected pickup site. Exactly one → preselect it (the
  // backend resolver would do the same; showing it makes it explicit).
  const pickupBoxes = useMemo(
    () => partnerBoxes.filter((p) => p.siteId === pickupSiteIdWatch),
    [partnerBoxes, pickupSiteIdWatch],
  );
  useEffect(() => {
    if (pickupMode !== 'site') return;
    if (pickupBoxes.length === 1) {
      form.setFieldsValue({ collectionPointId: pickupBoxes[0].id });
    } else {
      form.setFieldsValue({ collectionPointId: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupSiteIdWatch, pickupBoxes.length]);

  const siteOptions = useMemo(
    () =>
      partnerSites.map((s) => ({
        label: `${s.name} — ${s.address}`,
        value: s.id,
      })),
    [partnerSites],
  );

  const boxOptions = useMemo(
    () =>
      pickupBoxes.map((p) => ({
        label: `${p.name}${p.externalRef ? ` (${p.externalRef})` : ''}${p.capacityKg ? ` — ${p.capacityKg} kg` : ''}`,
        value: p.id,
      })),
    [pickupBoxes],
  );

  const handleOnFinish = async (values) => {
    const params = setParams(values);

    // OVO-253 — "Save as new site": create the Site first so this order (and
    // every future one at the same name+address) links to it. A failure (e.g.
    // duplicate) warns but never blocks the order itself.
    if (values.partnerId) {
      if (pickupMode === 'custom' && values.pickupSaveAsSite && params.pickupAddress) {
        const { response, error } = await createSite(values.partnerId, {
          name: params.pickupName,
          address: params.pickupAddress,
          lat: params.pickupAddressLat ?? undefined,
          lng: params.pickupAddressLng ?? undefined,
          phone: params.pickupPhone || undefined,
        });
        if (response?.id) params.pickupSiteId = response.id;
        else
          message.warning(`Could not save the pickup address as a site${error?.message ? `: ${error.message}` : ''}`);
      }
      if (
        orderType !== ORDER_TYPES.SERVICE &&
        deliveryMode === 'custom' &&
        values.deliverySaveAsSite &&
        params.deliveryAddress
      ) {
        const { response, error } = await createSite(values.partnerId, {
          name: params.deliveryName,
          address: params.deliveryAddress,
          lat: params.deliveryAddressLat ?? undefined,
          lng: params.deliveryAddressLng ?? undefined,
          phone: params.deliveryPhone || undefined,
        });
        if (response?.id) params.deliverySiteId = response.id;
        else
          message.warning(`Could not save the delivery address as a site${error?.message ? `: ${error.message}` : ''}`);
      }
    }

    handleSubmit && handleSubmit(params);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(doGetPartners({ take: 1000000 }));
  }, []);
  const setParams = (values) => {
    const params = { ...values };
    params.orderType = orderType;
    params.pickupPhone =
      !isEmpty(values.pickupPhone) && countryPickupPhone?.dialCode !== values.pickupPhone
        ? `${values.pickupPhone?.includes('+') ? '' : '+'}${values.pickupPhone}`
        : null;
    params.pickupAddress = values.pickupAddress?.address;
    params.pickupAddressLat = values.pickupAddress?.latLng?.lat;
    params.pickupAddressLng = values.pickupAddress?.latLng?.lng;
    params.pickupTimeStart = values.pickupTimeStart ? values.pickupTimeStart.format(TIME_FORMAT) : null;
    params.pickupTimeEnd = values.pickupTimeEnd ? values.pickupTimeEnd.format(TIME_FORMAT) : null;
    params.pickupDuration = values.pickupDuration ? Number(values.pickupDuration) : null;
    params.pickupEmail = values.pickupEmail || null;

    // OVO-253 — site mode overrides the custom fields with the catalog Site's
    // identity (name/address/coords/phone) and carries the explicit FKs.
    if (pickupMode === 'site' && values.pickupSiteId) {
      const site = partnerSites.find((s) => s.id === values.pickupSiteId);
      if (site) {
        params.pickupName = site.name;
        params.pickupAddress = site.address;
        params.pickupAddressLat = site.lat;
        params.pickupAddressLng = site.lng;
        if (site.phone) params.pickupPhone = site.phone;
        params.pickupSiteId = site.id;
      }
      params.collectionPointId = values.collectionPointId || null;
    } else {
      delete params.pickupSiteId;
      params.collectionPointId = null;
    }
    delete params.pickupSaveAsSite;

    // Delivery fields only for standard orders
    if (orderType !== ORDER_TYPES.SERVICE) {
      params.deliveryPhone =
        !isEmpty(values.deliveryPhone) && countryDeliveryPhone?.dialCode !== values.deliveryPhone
          ? `${values.deliveryPhone?.includes('+') ? '' : '+'}${values.deliveryPhone}`
          : null;
      params.deliveryEmail = values.deliveryEmail || null;
      params.deliveryAddress = values.deliveryAddress?.address;
      params.deliveryAddressLat = values.deliveryAddress?.latLng?.lat;
      params.deliveryAddressLng = values.deliveryAddress?.latLng?.lng;
      params.deliveryTimeStart = values.deliveryTimeStart ? values.deliveryTimeStart.format(TIME_FORMAT) : null;
      params.deliveryTimeEnd = values.deliveryTimeEnd ? values.deliveryTimeEnd.format(TIME_FORMAT) : null;
      params.deliveryDuration = values.deliveryDuration ? Number(values.deliveryDuration) : null;

      if (deliveryMode === 'site' && values.deliverySiteId) {
        const site = partnerSites.find((s) => s.id === values.deliverySiteId);
        if (site) {
          params.deliveryName = site.name;
          params.deliveryAddress = site.address;
          params.deliveryAddressLat = site.lat;
          params.deliveryAddressLng = site.lng;
          if (site.phone) params.deliveryPhone = site.phone;
          params.deliverySiteId = site.id;
        }
      } else {
        delete params.deliverySiteId;
      }
    } else {
      // Clear delivery fields for SERVICE
      params.deliveryPhone = null;
      params.deliveryEmail = null;
      params.deliveryAddress = null;
      params.deliveryAddressLat = null;
      params.deliveryAddressLng = null;
      params.deliveryTimeStart = null;
      params.deliveryTimeEnd = null;
      params.deliveryDuration = null;
      params.deliveryName = null;
      delete params.deliverySiteId;
    }
    delete params.deliverySaveAsSite;

    // SERVICE — a single-destination visit: the (relabeled) pickup section IS
    // the service location. The old hidden branch never set serviceAddress,
    // which left SERVICE orders un-optimizable (null service coords). The
    // pickup/delivery columns are NOT NULL + DTO-required, so mirror the
    // service identity into both — the same trick ensureRetourOrders uses.
    if (orderType === ORDER_TYPES.SERVICE) {
      params.serviceAddress = params.pickupAddress;
      params.serviceAddressLat = params.pickupAddressLat;
      params.serviceAddressLng = params.pickupAddressLng;
      params.serviceTimeStart = params.pickupTimeStart;
      params.serviceTimeEnd = params.pickupTimeEnd;
      params.serviceDuration = params.pickupDuration;
      params.deliveryName = params.pickupName;
      params.deliveryAddress = params.pickupAddress;
      params.deliveryAddressLat = params.pickupAddressLat;
      params.deliveryAddressLng = params.pickupAddressLng;
    }

    params.deliveryDate = values.deliveryDate ? values.deliveryDate.format(DATE_BACKEND_FORMAT) : null;
    params.sizeLength = values.sizeLength ? Number(values.sizeLength) : null;
    params.sizeWidth = values.sizeWidth ? Number(values.sizeWidth) : null;
    params.sizeHeight = values.sizeHeight ? Number(values.sizeHeight) : null;
    params.weight = values.weight ? Number(values.weight) : null;
    params.types = types;
    params.ticketNames = ticketNames;

    // Partner: send partnerId from the select, and also set partner string for backward compat
    if (values.partnerId) {
      const selectedPartner = partners.find((p) => p.id === values.partnerId);
      params.partnerId = values.partnerId;
      params.partner = selectedPartner?.code || selectedPartner?.name || null;
    } else {
      params.partnerId = null;
      params.partner = null;
    }

    params.partnerContact = values.partnerContact || null;
    params.partnerOrderRef = values.partnerOrderRef || null;
    params.tourRef = values.tourRef || null;
    params.airtableOrderRecordRef = values.airtableOrderRecordRef || null;
    params.preparationMode = values.preparationMode || null;
    params.collectionForm = values.collectionForm || null;

    // Product details
    if (values.partnerId && productTypes.length > 0) {
      params.productDetailsKnown = values.productDetailsKnown ?? true;
      if (values.productDetailsKnown && values.orderProducts?.length > 0) {
        params.orderProducts = values.orderProducts.map((p) => ({
          partnerItemTypeId: p.partnerItemTypeId,
          quantity: p.quantity,
          weight: p.weight !== undefined && p.weight !== null && p.weight !== '' ? Number(p.weight) : null,
          notes: p.notes || null,
        }));
      } else {
        params.orderProducts = [];
      }
    }

    return params;
  };

  const handleOnChangePickupFrom = (value) => {
    form.setFieldsValue({ pickupTimeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('pickupTimeEnd'))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue('pickupTimeEnd') < value) {
      form.setFields([
        {
          name: 'pickupTimeStart',
          errors: ['Shift End must be less than Shift Start'],
        },
      ]);
    }
  };

  const handleOnChangePickupTo = (value) => {
    form.setFieldsValue({ pickupTimeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('pickupTimeStart'))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue('pickupTimeStart') > value) {
      form.setFields([
        {
          name: 'pickupTimeEnd',
          errors: ['Pickup To must be greater than Pickup From'],
        },
      ]);
    }
  };

  const resetErrorPickupTime = () => {
    form.setFields([
      {
        name: 'pickupTimeStart',
        errors: [],
      },
      {
        name: 'pickupTimeEnd',
        errors: [],
      },
    ]);
  };

  const handleOnChangeDeliveryFrom = (value) => {
    form.setFieldsValue({ deliveryTimeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('deliveryTimeEnd'))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue('deliveryTimeEnd') < value) {
      form.setFields([
        {
          name: 'deliveryTimeStart',
          errors: ['Delivery From must be less than Delivery To'],
        },
      ]);
    }
  };

  const handleOnChangeDeliveryTo = (value) => {
    form.setFieldsValue({ deliveryTimeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('deliveryTimeStart'))) {
      resetErrorDeliveryTime();
      return;
    }
    if (form.getFieldValue('deliveryTimeStart') > value) {
      form.setFields([
        {
          name: 'deliveryTimeEnd',
          errors: ['Delivery To must be greater than Delivery From'],
        },
      ]);
    }
  };

  const resetErrorDeliveryTime = () => {
    form.setFields([
      {
        name: 'deliveryTimeStart',
        errors: [],
      },
      {
        name: 'deliveryTimeEnd',
        errors: [],
      },
    ]);
  };

  // Header toggle for one side (pickup / delivery). Site mode is only
  // offered when a partner is selected.
  const renderModeToggle = (mode, setMode) => {
    if (!selectedPartnerId) return null;
    return (
      <Button type="link" onClick={() => setMode(mode === 'site' ? 'custom' : 'site')}>
        {mode === 'site' ? 'Use a different address' : 'Pick a site instead'}
      </Button>
    );
  };

  return (
    <div className="OrderForm">
      <Spin spinning={isFetching}>
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleOnFinish}
          initialValues={{
            pickupAddress: {},
            deliveryAddress: {},
            pickupPhone: '+41',
            deliveryPhone: '+41',
            orderType: ORDER_TYPES.STANDARD,
            productDetailsKnown: true,
          }}
        >
          <div className="FormContent">
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">Order</div>
              </div>
              <div className="SectionContent">
                {/* Order type: STANDARD (pickup → delivery) or SERVICE — a
                    single-destination visit (site or address only). */}
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item label="Type" name="orderType">
                      <Radio.Group
                        value={orderType}
                        onChange={(e) => {
                          setOrderType(e.target.value);
                          form.setFieldsValue({ orderType: e.target.value });
                          // Clear delivery field errors when switching to SERVICE
                          if (e.target.value === ORDER_TYPES.SERVICE) {
                            form.setFields([
                              { name: 'deliveryName', errors: [] },
                              { name: 'deliveryAddress', errors: [] },
                            ]);
                          }
                        }}
                      >
                        <Radio.Button value={ORDER_TYPES.STANDARD}>Standard Delivery</Radio.Button>
                        <Radio.Button value={ORDER_TYPES.SERVICE}>Service</Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  {isSingleTenant ? (
                    <Form.Item name="partnerId" hidden>
                      <Input />
                    </Form.Item>
                  ) : (
                    <Col span={8}>
                      <Form.Item label="Partner" name="partnerId">
                        <Select
                          placeholder="Select a partner"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={partners.map((p) => ({
                            label: p.name,
                            value: p.id,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={isSingleTenant ? 12 : 8}>
                    <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
                      <Input placeholder="Name" />
                    </Form.Item>
                  </Col>
                  <Col span={isSingleTenant ? 12 : 8}>
                    <Form.Item
                      label="Delivery Date"
                      name="deliveryDate"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <DatePicker placeholder="Delivery Date" format={DAY_FORMAT} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
          <div className="FormContent">
            <div className="Section">
              <div className="SectionHeader">
                <div className="Left">{orderType === ORDER_TYPES.SERVICE ? 'Service location' : 'Pickup'}</div>
                <div className="Right">{renderModeToggle(pickupMode, setPickupMode)}</div>
              </div>
              <div className="SectionContent">
                {pickupMode === 'site' ? (
                  <Row gutter={24}>
                    <Col span={pickupBoxes.length > 0 ? 12 : 16}>
                      <Form.Item
                        label={orderType === ORDER_TYPES.SERVICE ? 'Service Site' : 'Pickup Site'}
                        name="pickupSiteId"
                        rules={[{ required: true, message: 'Required' }]}
                        extra={
                          partnerSites.length === 0
                            ? 'This partner has no sites yet — use a different address, or add sites from the partner page.'
                            : undefined
                        }
                      >
                        <Select
                          placeholder="Search a site (name or address)"
                          showSearch
                          allowClear
                          optionFilterProp="label"
                          options={siteOptions}
                        />
                      </Form.Item>
                    </Col>
                    {pickupBoxes.length > 0 && (
                      <Col span={8}>
                        <Form.Item
                          label="Collection box"
                          name="collectionPointId"
                          extra={
                            pickupBoxes.length === 1
                              ? 'This site has one box — preselected.'
                              : 'Several boxes at this site — pick the one being collected (optional).'
                          }
                        >
                          <Select
                            placeholder="Select a box (optional)"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            options={boxOptions}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                ) : (
                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item
                        label={orderType === ORDER_TYPES.SERVICE ? 'Service name' : 'Pickup Name'}
                        name="pickupName"
                        rules={[{ required: true, message: 'Required' }]}
                      >
                        <Input placeholder={orderType === ORDER_TYPES.SERVICE ? 'Service name' : 'Pickup Name'} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={orderType === ORDER_TYPES.SERVICE ? 'Service address' : 'Pickup Address'}
                        name="pickupAddress"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                                return Promise.resolve();
                              }

                              if (isEmpty(value?.address)) {
                                return Promise.reject(new Error('Required'));
                              }

                              return Promise.reject(new Error('Invalid'));
                            },
                          },
                        ]}
                      >
                        <AddressInput
                          placeholder="Pickup Address"
                          onSelect={(value) => form.setFieldsValue({ pickupAddress: value })}
                        />
                      </Form.Item>
                    </Col>
                    {selectedPartnerId && (
                      <Col span={6}>
                        <Form.Item label=" " name="pickupSaveAsSite" valuePropName="checked">
                          <Checkbox>Save as new site</Checkbox>
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                )}
                {showAdvanced && (
                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item label="Pickup Phone Number" name="pickupPhone">
                        <PhoneInput
                          country={'ch'}
                          size="normal"
                          prefix="+"
                          specialLabel=""
                          countryCodeEditable
                          enableSearch
                          disableSearchIcon
                          onChange={(value, country) => {
                            setCountryPickupPhone(country);
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Pickup From" name="pickupTimeStart">
                        <TimePicker
                          placeholder="Pickup From"
                          format={TIME_FORMAT}
                          minuteStep={30}
                          style={{ width: '100%' }}
                          onChange={handleOnChangePickupFrom}
                          onSelect={handleOnChangePickupFrom}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Pickup To" name="pickupTimeEnd">
                        <TimePicker
                          placeholder="Pickup To"
                          format={TIME_FORMAT}
                          minuteStep={30}
                          style={{ width: '100%' }}
                          onChange={handleOnChangePickupTo}
                          onSelect={handleOnChangePickupTo}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label="Pickup Email"
                        name="pickupEmail"
                        rules={[{ type: 'email', message: 'Invalid Email' }]}
                      >
                        <Input placeholder=" Pickup Email" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label={tOrders('form.pickupDuration')}
                        name="pickupDuration"
                        tooltip={tOrders('form.pickupDurationTooltip')}
                      >
                        <Input placeholder={tOrders('form.pickupDuration')} suffix={tOrders('common:minutes')} />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </div>
            </div>
          </div>
          {orderType !== ORDER_TYPES.SERVICE && (
            <div className="FormContent">
              <div className="Section">
                <div className="SectionHeader">
                  <div className="Left">Delivery</div>
                  <div className="Right">{renderModeToggle(deliveryMode, setDeliveryMode)}</div>
                </div>
                <div className="SectionContent">
                  {deliveryMode === 'site' ? (
                    <Row gutter={24}>
                      <Col span={16}>
                        <Form.Item
                          label="Delivery Site"
                          name="deliverySiteId"
                          dependencies={['pickupSiteId']}
                          rules={[
                            { required: orderType !== ORDER_TYPES.SERVICE, message: 'Required' },
                            // #131 — a collection delivered "to the box itself" puts a delivery
                            // right after every pickup; the delivery is where the load goes.
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  value &&
                                  pickupMode === 'site' &&
                                  orderType !== ORDER_TYPES.SERVICE &&
                                  value === getFieldValue('pickupSiteId')
                                ) {
                                  return Promise.reject(
                                    new Error(
                                      'Pickup and delivery are the same site. The delivery is where the driver takes the load (e.g. the depot); for a visit to a single place, use a Service order.',
                                    ),
                                  );
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          extra={
                            partnerSites.length === 0
                              ? 'This partner has no sites yet — use a different address, or add sites from the partner page.'
                              : undefined
                          }
                        >
                          <Select
                            placeholder="Search a site (name or address)"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            options={siteOptions}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  ) : (
                    <Row gutter={24}>
                      <Col span={6}>
                        <Form.Item
                          label="Delivery Name"
                          name="deliveryName"
                          rules={[{ required: orderType !== ORDER_TYPES.SERVICE, message: 'Required' }]}
                        >
                          <Input placeholder="Delivery Name" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="Delivery Address"
                          name="deliveryAddress"
                          rules={[
                            {
                              validator: (_, value) => {
                                if (orderType === ORDER_TYPES.SERVICE) {
                                  return Promise.resolve();
                                }
                                if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                                  return Promise.resolve();
                                }

                                if (isEmpty(value?.address)) {
                                  return Promise.reject(new Error('Required'));
                                }

                                return Promise.reject(new Error('Invalid'));
                              },
                            },
                          ]}
                        >
                          <AddressInput
                            placeholder="Delivery Address"
                            onSelect={(value) => form.setFieldsValue({ deliveryAddress: value })}
                          />
                        </Form.Item>
                      </Col>
                      {selectedPartnerId && (
                        <Col span={6}>
                          <Form.Item label=" " name="deliverySaveAsSite" valuePropName="checked">
                            <Checkbox>Save as new site</Checkbox>
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  )}
                  {showAdvanced && (
                    <Row gutter={24}>
                      <Col span={6}>
                        <Form.Item label="Delivery Phone Number" name="deliveryPhone">
                          <PhoneInput
                            country={'ch'}
                            prefix="+"
                            specialLabel=""
                            enableSearch
                            disableSearchIcon
                            onChange={(value, country) => {
                              setCountryDeliveryPhone(country);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="Delivery From" name="deliveryTimeStart">
                          <TimePicker
                            placeholder="Delivery From"
                            format={TIME_FORMAT}
                            minuteStep={30}
                            style={{ width: '100%' }}
                            onChange={handleOnChangeDeliveryFrom}
                            onSelect={handleOnChangeDeliveryFrom}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="Delivery To" name="deliveryTimeEnd">
                          <TimePicker
                            placeholder="Delivery To"
                            format={TIME_FORMAT}
                            minuteStep={30}
                            style={{ width: '100%' }}
                            onChange={handleOnChangeDeliveryTo}
                            onSelect={handleOnChangeDeliveryTo}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          label="Delivery Email"
                          name="deliveryEmail"
                          rules={[{ type: 'email', message: 'Invalid Email' }]}
                        >
                          <Input placeholder="Delivery Email" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          label={tOrders('form.deliveryDuration')}
                          name="deliveryDuration"
                          tooltip={tOrders('form.deliveryDurationTooltip')}
                        >
                          <Input placeholder={tOrders('form.deliveryDuration')} suffix={tOrders('common:minutes')} />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="FormContent">
            <Button
              type="dashed"
              block
              onClick={() => setShowAdvanced(!showAdvanced)}
              icon={showAdvanced ? <UpOutlined /> : <DownOutlined />}
            >
              {showAdvanced ? 'Hide advanced options' : 'Advanced options'}
            </Button>
          </div>
          {showAdvanced && (
            <div className="FormContent">
              <div className="Section">
                <div className="SectionHeader">
                  <div className="Left">Order Info</div>
                </div>
                <div className="SectionContent">
                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item label="Order Ref" name="orderRef">
                        <Input placeholder="Order Ref" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Volume" name="volume">
                        <InputNumber placeholder="Volume" step={0.001} style={{ width: '100%' }} suffix="m³" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="weight" label="Weight">
                        <Input min={1} type="number" suffix="kg" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item name="sizeLength" label="Size (LxWxH)">
                        <Input min={1} type="number" suffix="cm" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="sizeWidth" label=" ">
                        <Input min={1} type="number" suffix="cm" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="sizeHeight" label=" ">
                        <Input min={1} type="number" suffix="cm" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Description" name="description">
                        <Input.TextArea rows={4} placeholder="Description" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Pickup Note" name="pickupNote">
                        <Input.TextArea rows={4} placeholder="Pickup Note" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Delivery Note" name="deliveryNote">
                        <Input.TextArea rows={4} placeholder="Delivery Note" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Type" name="types">
                        <TagInput size="normal" tags={types} onChange={(values) => setTypes(values)} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Tickets" name="ticketNames">
                        <TagInput size="normal" tags={ticketNames} onChange={(values) => setTicketNames(values)} />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Order Products Section — only when partner has PRODUCT item types */}
                  {selectedPartnerId && productTypes.length > 0 && (
                    <>
                      <Divider orientation="left">Products</Divider>
                      <Row gutter={24}>
                        <Col span={24}>
                          <Form.Item name="productDetailsKnown" valuePropName="checked" style={{ marginBottom: 12 }}>
                            <Checkbox>Product details known at order creation</Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                      {productDetailsKnown && (
                        <Form.List name="orderProducts">
                          {(fields, { add, remove }) => (
                            <>
                              {fields.map(({ key, name, ...restField }) => (
                                <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                                  <Col span={8}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'partnerItemTypeId']}
                                      rules={[{ required: true, message: 'Select product' }]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Select
                                        placeholder="Select product"
                                        options={productTypes.map((t) => ({
                                          label: `${t.name}${t.unitOfMeasure ? ` (${t.unitOfMeasure})` : ''}`,
                                          value: t.id,
                                        }))}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={3}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'quantity']}
                                      rules={[{ required: true, message: 'Qty' }]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <InputNumber min={0.01} step={0.1} placeholder="Qty" style={{ width: '100%' }} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={4}>
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prev, cur) =>
                                        prev?.orderProducts?.[name]?.partnerItemTypeId !==
                                        cur?.orderProducts?.[name]?.partnerItemTypeId
                                      }
                                    >
                                      {({ getFieldValue }) => {
                                        const tid = getFieldValue(['orderProducts', name, 'partnerItemTypeId']);
                                        const type = productTypes.find((x) => x.id === tid);
                                        if (!type?.requiresWeight) return null;
                                        return (
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'weight']}
                                            style={{ marginBottom: 0 }}
                                            tooltip="Leave empty to require driver to enter weight at pickup"
                                          >
                                            <InputNumber
                                              min={0}
                                              step={0.1}
                                              placeholder={`Weight (${type.unitOfMeasure || 'kg'}) — driver fills if blank`}
                                              style={{ width: '100%' }}
                                              // Accept "," and "." as decimal separators. AntD's
                                              // default parser strips "," before Number() so "0,1"
                                              // would silently become 1.
                                              parser={(v) =>
                                                v == null
                                                  ? ''
                                                  : String(v)
                                                      .replace(/,/g, '.')
                                                      .replace(/[^\d.\-]/g, '')
                                              }
                                              formatter={(v) => (v == null || v === '' ? '' : String(v))}
                                            />
                                          </Form.Item>
                                        );
                                      }}
                                    </Form.Item>
                                  </Col>
                                  <Col span={7}>
                                    <Form.Item {...restField} name={[name, 'notes']} style={{ marginBottom: 0 }}>
                                      <Input placeholder="Notes (optional)" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={2}>
                                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                                  </Col>
                                </Row>
                              ))}
                              <Button
                                type="dashed"
                                onClick={() => add({ quantity: 1 })}
                                icon={<PlusOutlined />}
                                style={{ width: '50%' }}
                              >
                                Add Product
                              </Button>
                            </>
                          )}
                        </Form.List>
                      )}
                      <Divider />
                    </>
                  )}

                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item label="Partner Contact" name="partnerContact">
                        <Input placeholder="Partner Contact" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Partner Order Ref" name="partnerOrderRef">
                        <Input placeholder="Partner Order Ref" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Tour Ref" name="tourRef">
                        <Input placeholder="Tour Ref" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Airtable Order Record Ref" name="airtableOrderRecordRef">
                        <Input placeholder="Airtable Order Record Ref" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Preparation Mode" name="preparationMode">
                        <Input placeholder="Preparation Mode" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="Collection Form" name="collectionForm">
                        <Input placeholder="Collection Form" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          )}
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <Link to={'/orders'}>
                <Button key="cancel">Cancel</Button>
              </Link>
              <Button htmlType="submit" type="primary">
                Create
                <ReactSVG className="icon" src="/icons/submit.svg" />
              </Button>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default OrderForm;
